// Google Analytics 4 — anonymous, non-PII behavioral measurement.
//
// Rules enforced here:
//  - the gtag script is injected at most once, in the browser only
//  - SPA route changes send exactly one page_view per unique path
//  - event payloads are sanitized: no names, emails, phones, company names,
//    messages, tokenized deck URLs, or lead IDs ever leave the browser
//
// This file is intentionally free of any lead/attribution logic. UTM capture
// and database attribution live in src/lib/attribution.ts and are untouched.

export const GA_MEASUREMENT_ID = "G-1CKVZREV18";

export const ANALYTICS_EVENTS = [
  "investor_cta_click",
  "investor_form_start",
  "investor_form_submit",
  "investor_request_success",
  "partnership_cta_click",
  "partnership_form_start",
  "partnership_form_submit",
  "partnership_request_success",
  "media_cta_click",
  "media_form_start",
  "media_form_submit",
  "media_request_success",
  "section_view",
] as const;


export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[number];

/** Only short, enumerable, non-identifying values are allowed. */
export type AnalyticsParams = Record<string, string | number | boolean>;

/** Param keys that could ever carry personal or identifying data. */
const BLOCKED_KEY_PATTERN =
  /(name|email|mail|phone|tel|company|fund|message|note|token|deck_url|url|href|lead|id|user|address|ip)/i;

const ALLOWED_KEYS = new Set([
  "location_id",
  "placement",
  "section",
  "form",
  "variant",
  "value_range",
  "investor_type",
]);

const PII_VALUE_PATTERN =
  /(@|\+?\d[\d\s().-]{6,}|https?:\/\/|\/investor-deck\/|\/partnership-package\/|[0-9a-f]{8}-[0-9a-f]{4}|[A-Za-z0-9_-]{24,})/;

const MAX_VALUE_LENGTH = 40;

/**
 * Drops any key that is not an explicitly allowed, non-identifying dimension
 * and any value that looks like personal data, a URL, a token, or an ID.
 */
export function sanitizeEventParams(params?: AnalyticsParams): AnalyticsParams {
  const safe: AnalyticsParams = {};
  if (!params) return safe;
  for (const [key, raw] of Object.entries(params)) {
    if (raw === null || raw === undefined) continue;
    if (!ALLOWED_KEYS.has(key)) continue;
    if (BLOCKED_KEY_PATTERN.test(key)) continue;
    if (typeof raw === "boolean" || typeof raw === "number") {
      if (typeof raw === "number" && !Number.isFinite(raw)) continue;
      safe[key] = raw;
      continue;
    }
    if (typeof raw !== "string") continue;
    const value = raw.trim();
    if (!value || value.length > MAX_VALUE_LENGTH) continue;
    if (PII_VALUE_PATTERN.test(value)) continue;
    safe[key] = value;
  }
  return safe;
}

/** Strips query strings, hashes, and any token path segment from a route. */
export function sanitizePagePath(pathname: string): string {
  const path = (pathname || "/").split("?")[0]!.split("#")[0]!;
  return path
    .replace(/^(\/investor-deck)\/.+$/, "$1/:token")
    .replace(/^(\/partnership-package)\/.+$/, "$1/:token");
}

type PageViewTracker = { send: (pathname: string) => string | null };

/** One page_view per unique sanitized path — repeats are suppressed. */
export function createPageViewTracker(): PageViewTracker {
  let last: string | null = null;
  return {
    send(pathname: string) {
      const path = sanitizePagePath(pathname);
      if (path === last) return null;
      last = path;
      return path;
    },
  };
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __ccAnalyticsInitialized?: boolean;
  }
}

function enabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.location.hostname === "clutchcube.com" || window.location.hostname === "www.clutchcube.com";
  } catch {
    return false;
  }
}

/**
 * Defines window.dataLayer and window.gtag exactly the way gtag.js expects:
 * the command queue receives the raw `arguments` object, not an array — GA4
 * ignores plain-array commands, which is why no hits were transmitted.
 */
export function ensureGtag(): ((...args: unknown[]) => void) | null {
  if (typeof window === "undefined") return null;
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      };
    }
    return window.gtag;
  } catch {
    // Blocked storage / extension interference — measurement is best-effort.
    return null;
  }
}

function gtag(...args: unknown[]): void {
  const fn = ensureGtag();
  if (!fn) return;
  try {
    fn(...args);
  } catch {
    // Best-effort measurement.
  }
}

/** Injects gtag.js exactly once. Safe to call on every mount. */
export function initAnalytics(): void {
  if (!enabled()) return;
  if (window.__ccAnalyticsInitialized) return;
  try {
    // The queue and shim must exist BEFORE the tag loads and before any config.
    if (!ensureGtag()) return;
    window.__ccAnalyticsInitialized = true;

    if (!document.querySelector(`script[data-ga-id="${GA_MEASUREMENT_ID}"]`)) {
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      script.dataset.gaId = GA_MEASUREMENT_ID;
      document.head.appendChild(script);
    }

    gtag("js", new Date());
    // Manual page views: TanStack Router owns navigation.
    gtag("config", GA_MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });
  } catch {
    // No-op if the document is unavailable or the script is blocked.
  }
}

const pageViews = createPageViewTracker();

export function trackPageView(pathname: string): void {
  if (!enabled()) return;
  const path = pageViews.send(pathname);
  if (!path) return;
  gtag("event", "page_view", { page_path: path, page_title: document.title });
}

/**
 * Browser entry point used by the mounted root Analytics component.
 * Initialization and the first page view intentionally share one call so the
 * gtag shim and config command always exist before an event can be queued.
 */
export function initializeAnalytics(pathname: string): void {
  initAnalytics();
  trackPageView(pathname);
}

export function trackEvent(event: AnalyticsEvent, params?: AnalyticsParams): void {
  if (!enabled()) return;
  gtag("event", event, sanitizeEventParams(params));
}

/** Sections that may report a single view per session. */
export const TRACKED_SECTIONS = [
  "top",
  "why-now",
  "why-sports",
  "moments",
  "technology",
  "flagship",
  "business",
  "franchise",
  "invest",
] as const;

const seenSections = new Set<string>();

/** Fires section_view at most once per section per page session. */
export function trackSectionView(section: string): void {
  if (!enabled()) return;
  if (seenSections.has(section)) return;
  seenSections.add(section);
  trackEvent("section_view", { section });
}
